import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import VorionLogo from "../components/VorionLogo";

const HERO_IMG = "https://images.unsplash.com/photo-1564501049412-61c2a3083791?auto=format&fit=crop&w=1600&q=70";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/home");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0B0B0E]" data-testid="login-page">
      <div
        className="hidden md:flex flex-1 relative items-end p-12"
        style={{
          backgroundImage: `linear-gradient(110deg, rgba(11,11,14,0.92), rgba(11,11,14,0.55) 60%, rgba(11,11,14,0.2)), url(${HERO_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 max-w-md">
          <div className="mb-6">
            <VorionLogo variant="mark" size={56} testid="login-brand-hero" />
          </div>
          <h1 className="text-5xl font-extrabold text-white tracking-tight leading-[1.05] mb-4">
            One <span className="text-[#D4A968]">command room</span><br/> for every property.
          </h1>
          <p className="text-zinc-300 text-sm leading-relaxed max-w-sm">
            Coordinate Luxury, Elegance, Resort and Smart pre-openings on a single board.
            Pre-loaded Vorion templates, Vorion Safety audits, AI summaries — built for hospitality.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-2">
            {[
              { v: "5,000+", l: "Properties orchestrated" },
              { v: "8 langs", l: "Multilingual ready" },
              { v: "SOC II", l: "Enterprise-grade" },
            ].map((s, i) => (
              <div key={i} className="border border-zinc-700/60 bg-black/30 p-3 backdrop-blur-sm rounded">
                <div className="text-[#D4A968] text-base font-bold">{s.v}</div>
                <div className="text-[10px] text-zinc-400 uppercase font-mono tracking-wider mt-0.5">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="md:hidden mb-8">
            <VorionLogo variant="mark" size={48} testid="login-brand-mobile" />
          </div>
          <h2 className="text-3xl font-bold text-white tracking-tight mb-1">Welcome back</h2>
          <p className="text-sm text-zinc-400 mb-8">Sign in to your hotel operations workspace.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1.5">Email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                data-testid="login-email"
                className="w-full bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded-md px-3 py-2.5 text-sm text-white outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1.5">Password</label>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                data-testid="login-password"
                className="w-full bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded-md px-3 py-2.5 text-sm text-white outline-none transition-colors"
              />
            </div>
            {err && (
              <div data-testid="login-error" className="text-xs bg-[#EF4444]/10 border border-[#EF4444]/40 text-[#EF4444] rounded p-2.5">{err}</div>
            )}
            <button
              type="submit" disabled={loading} data-testid="login-submit"
              className="w-full bg-[#D4A968] hover:bg-[#E8C285] text-black font-semibold py-3 rounded-md text-sm transition-colors disabled:opacity-50"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-xs text-zinc-500">
            New to Vorion?{" "}
            <Link to="/register" data-testid="register-link" className="text-[#D4A968] hover:text-[#E8C285]">Create workspace →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
